import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'

import { CATERING_HOME, FRANCHISE_HOME, matchesRouteBase } from './platformRouting'

class PrivilegeService {
  constructor() {
    this.privileges = null
    this.privilegeMap = null
    this.loadPrivileges()
  }

  loadPrivileges() {
    const token = Cookies.get('auth_token')
    if (!token) {
      this.privileges = null
      this.privilegeMap = null
      return
    }

    try {
      const decoded = jwtDecode(token)
      if (decoded.merchant || decoded.franchise || decoded.franchise_id) {
        this.privileges = []
        this.privilegeMap = {
          global: {
            global: {
              can_create: true,
              can_read: true,
              can_update: true,
              can_delete: true
            },
            subResources: {}
          }
        }
      } else {
        this.privileges = decoded.privileges || []
        this.buildPrivilegeMap()
      }
    } catch (error) {
      console.error('Error decoding token:', error)
      this.privileges = null
      this.privilegeMap = null
    }
  }

  buildPrivilegeMap() {
    if (!this.privileges || !Array.isArray(this.privileges)) {
      this.privilegeMap = {}
      return
    }

    const map = {}

    this.privileges.forEach(privilege => {
      const resource = privilege.resource
      const subResource = privilege.sub_resource

      if (!map[resource]) {
        map[resource] = {
          global: null,
          subResources: {}
        }
      }

      if (subResource === 'sub_global' || subResource === null) {
        map[resource].global = {
          can_create: privilege.can_create,
          can_read: privilege.can_read,
          can_update: privilege.can_update,
          can_delete: privilege.can_delete
        }
      } else {
        map[resource].subResources[subResource] = {
          can_create: privilege.can_create,
          can_read: privilege.can_read,
          can_update: privilege.can_update,
          can_delete: privilege.can_delete
        }
      }
    })
    this.privilegeMap = map
  }

  hasGlobalAccess(resource) {
    if (!this.privilegeMap || !this.privilegeMap[resource]) {
      return false
    }

    const globalPriv = this.privilegeMap[resource].global
    return globalPriv !== null && globalPriv !== undefined
  }

  hasSubResourceAccess(resource, subResource) {
    if (!this.privilegeMap || !this.privilegeMap[resource]) {
      return false
    }

    if (this.hasGlobalAccess(resource)) {
      return true
    }

    return this.privilegeMap[resource].subResources[subResource] !== undefined
  }

  canAccessResourceAction(resource, subResource, action) {
    if (this.privilegeMap && this.privilegeMap['global']) {
      return true
    }

    if (!this.privilegeMap || !this.privilegeMap[resource]) {
      return false
    }

    const actionMap = {
      create: 'can_create',
      read: 'can_read',
      update: 'can_update',
      delete: 'can_delete'
    }

    const actionKey = actionMap[action]
    if (!actionKey) {
      return false
    }

    if (subResource) {
      const subResourcePrivilege = this.privilegeMap[resource].subResources[subResource]
      if (!subResourcePrivilege) {
        return false
      }

      return subResourcePrivilege[actionKey]
    } else {
      if (this.hasGlobalAccess(resource)) {
        return this.privilegeMap[resource].global[actionKey]
      }

      return false
    }
  }

  canAccessRoute(routePath) {
    this.loadPrivileges()
    if (this.privilegeMap && this.privilegeMap['global']) {
      return true
    }

    if (!this.privileges || this.privileges.length === 0) {
      return false
    }

    const routeMap = [
      { route: '/users/roles', resource: 'system_users' },
      { route: '/roles', resource: 'system_users' },
      { route: '/system_users', resource: 'system_users' },
      { route: '/store', resource: 'sell' },
      // Site settings is a Sell section now, so Sell opens it. `site` stays
      // beside it: it is still what the storefront pages check before they let
      // anything be edited, and it is still a role you can be given on its own.
      { route: '/storefront', resources: ['sell', 'site'] },
      { route: '/orders', resource: 'orders' },
      { route: '/messages', resource: 'messages' },
      { route: '/lists', resource: 'customers' },
      { route: '/resellers', resource: 'customers' },
      { route: '/reports', resource: 'reports' },
      { route: '/revenue-summary', resource: 'reports' },
      { route: '/payout-summary', resource: 'reports' },
      { route: '/orders-summary', resource: 'reports' },
      { route: '/recurring-membership-fees', resource: 'reports' },
      { route: '/products-sold-breakdown', resource: 'reports' },
      { route: '/upsell-sales', resource: 'reports' },
      { route: '/meal-plans', resource: 'reports' },
      { route: '/discount-code-usage', resource: 'reports' },
      { route: '/referral-code-usage', resource: 'reports' },
      { route: '/customer-growth-by-tier', resource: 'reports' },
      { route: '/membership-tier-growth', resource: 'reports' },
      { route: '/revenue-by-membership-tier', resource: 'reports' },
      { route: '/sell', resource: 'sell' },
      { route: '/automation', resource: 'automation' },
      { route: '/ai', resource: 'sell' },
      { route: '/marketing', resource: 'sell' },
      { route: '/leads', resource: 'sell' },
      { route: '/agent-settings', resource: 'sell' }
    ]

    for (const config of routeMap) {
      if (matchesRouteBase(routePath, config.route)) {
        const resources = config.resources ?? [config.resource]
        return resources.some(resource => this.canAccessResourceAction(resource, null, 'read'))
      }
    }

    return true
  }

  getDefaultRoute(platform = 'ecommerce') {
    if (platform === 'catering') return CATERING_HOME
    if (platform === 'franchise') return FRANCHISE_HOME

    const defaultRoutes = [
      '/orders',
      '/messages',
      '/lists',
      '/sell',
      '/reports',
      '/system_users',
      '/automation',
      '/storefront'
    ]

    for (const route of defaultRoutes) {
      if (this.canAccessRoute(route)) {
        return route
      }
    }

    return '/orders'
  }

  isMerchant() {
    const token = Cookies.get('auth_token')
    if (!token) {
      return false
    }

    try {
      const decoded = jwtDecode(token)
      return !!decoded.merchant
    } catch (error) {
      console.error('Error decoding token:', error)
      return false
    }
  }

  getTokenClaims() {
    const token = Cookies.get('auth_token')
    if (!token) {
      return {}
    }

    try {
      return jwtDecode(token)
    } catch {
      return {}
    }
  }

  getSystemUserEmail() {
    const token = Cookies.get('auth_token')
    if (!token) {
      return null
    }

    try {
      const decoded = jwtDecode(token)
      return decoded.system_user_email || null
    } catch (error) {
      console.error('Error decoding token:', error)
      return null
    }
  }

  getSystemUserId() {
    const token = Cookies.get('auth_token')
    if (!token) {
      return null
    }

    try {
      const decoded = jwtDecode(token)
      return decoded.system_user_id ?? null
    } catch (error) {
      console.error('Error decoding token:', error)
      return null
    }
  }

  isAdmin() {
    const token = Cookies.get('auth_token')
    if (!token) {
      return false
    }

    try {
      const decoded = jwtDecode(token)
      return !!decoded.role?.is_admin
    } catch (error) {
      console.error('Error decoding token:', error)
      return false
    }
  }

  isDefaultMerchantUser() {
    const token = Cookies.get('auth_token')
    if (!token) {
      return false
    }

    try {
      const decoded = jwtDecode(token)
      return !!decoded.default_merchant_user
    } catch (error) {
      console.error('Error decoding token:', error)
      return false
    }
  }
}

export default new PrivilegeService()
